'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { cachedRequest, invalidateCachePrefix } from '@/lib/supabase/request-cache';

export type UserRole = 'admin' | 'teacher' | 'family' | 'teacher_checkin' | 'ops_admin' | 'super_admin' | null;

// Role tier helpers — exported so middleware and guards can use them
export const ADMIN_ROLES: UserRole[] = ['admin', 'teacher_checkin', 'ops_admin', 'super_admin'];
export const OPS_ADMIN_ROLES: UserRole[] = ['admin', 'ops_admin', 'super_admin'];
export const SUPER_ADMIN_ROLES: UserRole[] = ['admin', 'super_admin'];
export const TEACHER_CHECKIN_ROLES: UserRole[] = ['admin', 'teacher_checkin', 'ops_admin', 'super_admin'];

interface AuthContextValue {
  user: any;
  session: any;
  loading: boolean;
  role: UserRole;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isOpsAdmin: boolean;
  isTeacherCheckin: boolean;
  isTeacher: boolean;
  isFamily: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

async function fetchUserRole(
  supabase: ReturnType<typeof createClient>,
  userId: string,
  userEmail: string
): Promise<UserRole> {
  return cachedRequest(
    `role:${userId}`,
    async () => {
      try {
        const adminEmails = (process.env.NEXT_PUBLIC_ADMIN_EMAILS || '')
          .split(',')
          .map((e) => e.trim().toLowerCase())
          .filter(Boolean);

        const { data } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', userId)
          .maybeSingle();

        if (data?.role) {
          if (['teacher_checkin', 'ops_admin', 'super_admin'].includes(data.role)) {
            return data.role as UserRole;
          }
          if (data.role === 'admin' || adminEmails.includes(userEmail.toLowerCase())) {
            return 'super_admin';
          }
          return data.role as UserRole;
        }

        if (adminEmails.includes(userEmail.toLowerCase())) return 'super_admin';
        return 'family';
      } catch {
        return 'family';
      }
    },
    10 * 60_000
  );
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<UserRole>(null);

  const supabase = createClient();

  useEffect(() => {
    let mounted = true;

    // Bootstrap: read current session once on mount
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s);
      setUser(s?.user ?? null);

      if (s?.user) {
        const r = await fetchUserRole(supabase, s.user.id, s.user.email ?? '');
        if (mounted) setRole(r);
      }

      if (mounted) setLoading(false);
    });

    // Listen for auth state changes (sign in, sign out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, s) => {
      if (!mounted) return;

      if (event === 'SIGNED_OUT' || !s) {
        setSession(null);
        setUser(null);
        setRole(null);
        setLoading(false);
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        // Just update the session — no need to re-fetch role
        setSession(s);
        return;
      }

      setSession(s);
      setUser(s.user);

      if (s.user) {
        const r = await fetchUserRole(supabase, s.user.id, s.user.email ?? '');
        if (mounted) setRole(r);
      }

      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    if (user?.id) {
      invalidateCachePrefix(`role:${user.id}`);
      invalidateCachePrefix(`profile:${user.id}`);
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const { data: { user: u }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return u;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async () => {
    if (!user) return null;
    return cachedRequest(
      `profile:${user.id}`,
      async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
        if (error) throw error;
        return data;
      },
      5 * 60_000
    );
  };

  const value: AuthContextValue = {
    user,
    session,
    loading,
    role,
    // isAdmin = any admin tier (backward compat for existing code that checks isAdmin)
    isAdmin: ADMIN_ROLES.includes(role),
    isSuperAdmin: SUPER_ADMIN_ROLES.includes(role),
    isOpsAdmin: OPS_ADMIN_ROLES.includes(role),
    isTeacherCheckin: TEACHER_CHECKIN_ROLES.includes(role),
    isTeacher: role === 'teacher',
    isFamily: role === 'family',
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
